import { useDraggable, useDroppable } from '@dnd-kit/react'
import type { MatchingItem as MatchingItemType } from '@vedalib/editor/lib/elements'
import { textToRtValue } from '@vedalib/rich-text'
import cn from 'classnames'
import * as R from 'ramda'
import { useCallback } from 'react'
import { useContext } from 'use-context-selector'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import RichText from 'components/form/RichText'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import TestMatchingContext from '../TestMatchingContext'
import * as s from './DefaultElementEditor.module.scss'
import MatchingItem from './MatchingItem'

const SORT_PREFIX = 'sort-'

const genName = (index: number, side: 'leftLabel' | 'rightLabel') => `portalItem.${index}.${side}`

interface IDefaultElementEditorItemProps {
  index: number
  item: MatchingItemType
  isDraggingInList: boolean
}

const DefaultElementEditorItem: React.FC<IDefaultElementEditorItemProps> = ({
  index,
  isDraggingInList,
  item,
}) => {
  const {
    styles,
    value,
    isActiveElement,
    onLabelSelect,
    activeIndex,
    onChangeLabel,
    isFill,
    font,
    onChange,
    waiting,
    feedback,
    mode,
  } = useContext(TestMatchingContext)
  const feedbackValue = getFeedbackValue(value)

  const { ref: dragRef, isDragging } = useDraggable({ id: item.value, data: item })
  const { ref: dropRef } = useDroppable({ id: `${SORT_PREFIX}${item.value}`, data: item })

  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      ;(dragRef as React.RefCallback<HTMLDivElement>)(el)
      ;(dropRef as React.RefCallback<HTMLDivElement>)(el)
    },
    [dragRef, dropRef],
  )

  const handleDelete = (item: MatchingItemType) => () =>
    onChange?.(
      R.assoc(
        'items',
        value.items.filter(({ value: id }) => id !== item.value),
        value,
      ),
    )

  return (
    <div
      className={cn(s.row, { [s.active]: activeIndex === index.toString() })}
      ref={ref}
      style={{
        marginBottom: styles.indents.marginBottom,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <div className={s.item}>
        <TestFeedback
          feedbackValue={feedbackValue}
          font={font}
          mode={mode}
          styles={feedback}
          isTrueAnswer
          showAnswerFb
        >
          <MatchingItem font={font} index={index} mode='left' styles={styles}>
            <div
              className={s.text}
              onMouseDown={(e) => onLabelSelect(genName(index, 'leftLabel'), e)}
            >
              <RichText
                active={isActiveElement && activeIndex === genName(index, 'leftLabel')}
                disabled={!isFill}
                isPreview={mode?.previewMode !== PreviewMode.editor}
                name={genName(index, 'leftLabel')}
                onChange={(val) => onChangeLabel(val, 'leftLabel', index)}
                styles={font}
                value={item.leftLabel || textToRtValue('')}
                waiting={waiting}
              />
            </div>
          </MatchingItem>
        </TestFeedback>
      </div>
      <div className={s.item}>
        <MatchingItem font={font} index={index} mode='right' styles={styles}>
          <div
            className={s.text}
            onMouseDown={(e) => onLabelSelect(genName(index, 'rightLabel'), e)}
          >
            <RichText
              active={isActiveElement && activeIndex === genName(index, 'rightLabel')}
              disabled={!isFill}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={genName(index, 'rightLabel')}
              onChange={(val) => onChangeLabel(val, 'rightLabel', index)}
              styles={font}
              value={item.rightLabel || textToRtValue('')}
              waiting={waiting}
            />
          </div>
        </MatchingItem>
      </div>
      {isFill && isActiveElement && !isDraggingInList && (
        <AbsolutePortal
          name='deleteMultipleChoiceItem'
          placement='right'
          translateX='11px'
          translateY='-50%'
        >
          <Tooltip overlay={t('uiKit.tooltip.deleteAnswer')}>
            <IconButton
              disabled={value.items.length < 3}
              icon='otherClose'
              name='deleteMultipleChoiceItem'
              onClick={handleDelete(item)}
              size={KitSize.XS}
              styleType='secondary'
            />
          </Tooltip>
        </AbsolutePortal>
      )}
    </div>
  )
}

export default DefaultElementEditorItem
